import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { 
  Truck, 
  ArrowLeft,
  Mail,
  MailOpen,
  FileText,
  Download,
  Euro,
  Package,
  LogOut,
  Inbox as InboxIcon,
  Bell
} from "lucide-react";
import { toast } from "sonner";

export default function Inbox() {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/messages`, { headers });
      setMessages(response.data);
    } catch (e) {
      console.error("Error fetching messages:", e);
      toast.error("Fehler beim Laden der Nachrichten");
    } finally {
      setLoading(false);
    }
  };

  const openMessage = async (message) => {
    setSelectedMessage(message);
    
    // Mark as read
    if (!message.is_read) {
      try {
        await axios.post(`${API}/messages/${message.id}/read`, {}, { headers });
        setMessages(messages.map(m => 
          m.id === message.id ? { ...m, is_read: true } : m
        ));
      } catch (e) {
        console.error("Error marking as read:", e);
      }
    }
  };

  const handleDownloadAttachment = async (message) => {
    if (!message.attachment_file) return;
    
    try {
      let response;
      let filename;
      
      // Check if it's a label (has order_id and message_type is "label") or a general attachment
      if (message.message_type === "label" && message.order_id) {
        response = await axios.get(`${API}/orders/${message.order_id}/label`, {
          headers,
          responseType: "blob"
        });
        filename = `label_${message.order_number}.pdf`;
      } else {
        // General attachment
        response = await axios.get(`${API}/messages/attachment/${message.attachment_file}`, {
          headers,
          responseType: "blob"
        });
        filename = message.attachment_file;
      }
      
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      toast.success("Datei heruntergeladen");
    } catch (e) {
      toast.error("Fehler beim Herunterladen");
    }
  };

  const handleDeleteMessage = async (messageId, e) => {
    e.stopPropagation();
    if (!confirm("Nachricht wirklich löschen?")) return;
    
    try {
      await axios.delete(`${API}/messages/${messageId}`, { headers });
      setMessages(messages.filter(m => m.id !== messageId));
      if (selectedMessage?.id === messageId) setSelectedMessage(null);
      toast.success("Nachricht gelöscht");
    } catch (e) {
      toast.error("Fehler beim Löschen");
    }
  };

  const getMessageIcon = (type, isRead) => {
    if (type === "label") return <Package className={`h-6 w-6 ${isRead ? "text-slate-400" : "text-blue-600"}`} />;
    if (type === "invoice") return <Euro className={`h-6 w-6 ${isRead ? "text-slate-400" : "text-green-600"}`} />;
    return isRead ? <MailOpen className="h-6 w-6 text-slate-400" /> : <Mail className="h-6 w-6 text-blue-600" />;
  };

  const getMessageBadge = (type) => {
    if (type === "label") return <Badge className="bg-blue-100 text-blue-800">Label</Badge>;
    if (type === "invoice") return <Badge className="bg-green-100 text-green-800">Rechnung</Badge>;
    return <Badge className="bg-slate-100 text-slate-800">Nachricht</Badge>;
  };

  const unreadCount = messages.filter(m => !m.is_read).length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-slate-900 text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <Truck className="h-6 w-6 text-white" />
              </div>
              <div>
                <span className="font-bold text-lg">SonderCargo.de</span>
                <span className="text-xs text-slate-400 block">Medical, Weapons and much more</span>
              </div>
            </div>
            
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/dashboard" className="text-slate-300 hover:text-white transition" data-testid="nav-dashboard">
                Dashboard
              </Link>
              <Link to="/inbox" className="text-white font-medium relative" data-testid="nav-inbox">
                Posteingang
                {unreadCount > 0 && (
                  <span className="absolute -top-2 -right-4 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {unreadCount}
                  </span>
                )}
              </Link>
              <Link to="/support" className="text-slate-300 hover:text-white transition" data-testid="nav-support">
                Support
              </Link>
            </nav>
            
            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <p className="font-medium">{user?.name}</p>
                <p className="text-xs text-slate-400 uppercase">{user?.role}</p>
              </div>
              <Button variant="ghost" size="icon" className="text-slate-300 hover:text-white" onClick={logout} data-testid="logout-btn">
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="h-1 bg-gradient-to-r from-blue-600 via-blue-500 to-blue-400" />

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/dashboard" className="inline-flex items-center text-slate-600 hover:text-slate-900 mb-6" data-testid="back-link">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Zurück zum Dashboard
        </Link>

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" data-testid="page-title">Posteingang</h1>
            <p className="text-slate-500">
              {unreadCount > 0 ? `${unreadCount} ungelesene Nachricht(en)` : "Alle Nachrichten gelesen"}
            </p>
          </div>
          {unreadCount > 0 && (
            <div className="flex items-center gap-2 text-blue-600">
              <Bell className="h-5 w-5" />
              <span className="font-medium">{unreadCount} neu</span>
            </div>
          )}
        </div>

        {/* Messages List */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="border-b">
            <CardTitle className="flex items-center gap-2">
              <InboxIcon className="h-5 w-5" />
              Nachrichten
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {messages.map(message => (
                <div 
                  key={message.id}
                  className={`p-4 hover:bg-slate-50 transition cursor-pointer flex items-center gap-4 ${
                    !message.is_read ? "bg-blue-50/50" : ""
                  }`}
                  onClick={() => openMessage(message)}
                  data-testid={`message-${message.id}`}
                >
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                    !message.is_read ? "bg-white shadow-sm" : "bg-slate-100"
                  }`}>
                    {getMessageIcon(message.message_type, message.is_read)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className={`font-semibold truncate ${!message.is_read ? "text-slate-900" : "text-slate-600"}`}>
                        {message.subject}
                      </p>
                      {getMessageBadge(message.message_type)}
                      {!message.is_read && (
                        <span className="w-2 h-2 bg-blue-600 rounded-full" />
                      )}
                    </div>
                    <p className="text-sm text-slate-500 truncate">{message.content.substring(0, 100)}...</p>
                    <p className="text-xs text-slate-400 mt-1">
                      Von: {message.sender_name} • {message.created_at?.split("T")[0]}
                    </p>
                  </div>
                  {message.attachment_file && (
                    <div className="flex-shrink-0">
                      <Badge variant="outline" className="text-xs">
                        <FileText className="h-3 w-3 mr-1" />
                        Anhang
                      </Badge>
                    </div>
                  )}
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="text-slate-400 hover:text-red-600 flex-shrink-0"
                    onClick={(e) => handleDeleteMessage(message.id, e)}
                    title="Nachricht löschen"
                    data-testid={`delete-message-${message.id}`}
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </Button>
                </div>
              ))}
              
              {messages.length === 0 && (
                <div className="p-12 text-center">
                  <InboxIcon className="h-16 w-16 mx-auto text-slate-300 mb-4" />
                  <h3 className="font-semibold text-slate-900 mb-2">Posteingang leer</h3>
                  <p className="text-slate-500">Sie haben noch keine Nachrichten erhalten.</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Message Detail Modal */}
      <Dialog open={!!selectedMessage} onOpenChange={() => setSelectedMessage(null)}>
        <DialogContent className="sm:max-w-2xl" data-testid="message-detail-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedMessage && getMessageIcon(selectedMessage.message_type, true)}
              {selectedMessage?.subject}
            </DialogTitle>
          </DialogHeader>
          
          {selectedMessage && (
            <div className="space-y-4">
              <div className="flex items-center justify-between text-sm text-slate-500 border-b pb-4">
                <div>
                  <p>Von: <span className="font-medium text-slate-700">{selectedMessage.sender_name}</span></p>
                  <p>Datum: {selectedMessage.created_at?.split("T")[0]}</p>
                </div>
                {getMessageBadge(selectedMessage.message_type)}
              </div>
              
              {selectedMessage.order_number && (
                <div className="bg-slate-50 rounded-lg p-3 text-sm">
                  <span className="text-slate-500">Bezug auf Sendung: </span>
                  <Link 
                    to={`/order/${selectedMessage.order_id}`}
                    className="text-blue-600 hover:underline font-medium"
                  >
                    {selectedMessage.order_number}
                  </Link>
                </div>
              )}
              
              <div className="prose prose-sm max-w-none">
                <div className="whitespace-pre-wrap text-slate-700 bg-white border rounded-lg p-4">
                  {selectedMessage.content}
                </div>
              </div>
              
              {selectedMessage.attachment_file && (
                <div className="border rounded-lg p-4 bg-blue-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <FileText className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">Anhang verfügbar</p>
                        <p className="text-sm text-slate-500">{selectedMessage.attachment_file}</p>
                      </div>
                    </div>
                    <Button 
                      onClick={() => handleDownloadAttachment(selectedMessage)}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="download-attachment-btn"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Herunterladen
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
